
import { Link } from 'react-router-dom'

const HowItWorks = () => {
    return (
        <div className="pb-24">
            {/* Hero */}
            <section className="mx-auto w-full max-w-6xl px-6 py-20 text-center">
                <div className="reveal">
                    <p className="text-sm font-semibold uppercase tracking-widest text-emerald-600">
                        Workflow
                    </p>
                    <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight text-slate-900 md:text-6xl">
                        From localhost to <span className="text-emerald-600">https</span>
                    </h1>
                    <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-600">
                        We've removed the barriers between your code and the cloud. No YAML wrestling, no server provisioning.
                    </p>
                </div>
            </section>

            {/* Steps Vertical Timeline */}
            <section className="mx-auto w-full max-w-5xl px-6">
                <div className="relative border-l-2 border-slate-200 ml-4 md:ml-12 space-y-20 pb-20">
                    {/* Step 1 */}
                    <div className="relative pl-8 md:pl-16 reveal">
                        <div className="absolute -left-[9px] top-0 flex h-5 w-5 items-center justify-center rounded-full bg-white ring-4 ring-slate-100">
                            <div className="h-2.5 w-2.5 rounded-full bg-slate-900" />
                        </div>
                        <div className="grid gap-8 md:grid-cols-2">
                            <div>
                                <div className="text-sm font-bold uppercase tracking-widest text-slate-400">Step 01</div>
                                <h3 className="mt-2 text-3xl font-bold text-slate-900">Authenticate</h3>
                                <p className="mt-4 text-lg text-slate-600">
                                    Login securely via the CLI. We use a device authentication flow, so you don't need to manage long-lived API keys on your local machine.
                                </p>
                                <div className="mt-6 flex items-center gap-3 text-sm font-medium text-slate-500">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100">C</span>
                                    <span>CLI Based</span>
                                </div>
                            </div>
                            <div className="rounded-xl bg-slate-900 p-6 shadow-xl ring-1 ring-white/10">
                                <div className="font-mono text-sm text-slate-300">
                                    <div className="flex gap-2">
                                        <span className="text-emerald-400">$</span>
                                        <span className="text-white">inflynx login</span>
                                    </div>
                                    <div className="mt-4 text-slate-400">Opening browser for authentication...</div>
                                    <div className="mt-2 text-emerald-400">Success! Logged in as user@example.com</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Step 2 */}
                    <div className="relative pl-8 md:pl-16 reveal" style={{ animationDelay: '100ms' }}>
                        <div className="absolute -left-[9px] top-0 flex h-5 w-5 items-center justify-center rounded-full bg-white ring-4 ring-slate-100">
                            <div className="h-2.5 w-2.5 rounded-full bg-slate-900" />
                        </div>
                        <div className="grid gap-8 md:grid-cols-2">
                            <div>
                                <div className="text-sm font-bold uppercase tracking-widest text-slate-400">Step 02</div>
                                <h3 className="mt-2 text-3xl font-bold text-slate-900">Deploy</h3>
                                <p className="mt-4 text-lg text-slate-600">
                                    Run the deploy command in your project root. We zip your code, upload it to a secure build environment, and detect your runtime settings automatically.
                                </p>
                            </div>
                            <div className="rounded-xl bg-slate-900 p-6 shadow-xl ring-1 ring-white/10">
                                <div className="font-mono text-sm text-slate-300">
                                    <div className="flex gap-2">
                                        <span className="text-emerald-400">$</span>
                                        <span className="text-white">inflynx deploy</span>
                                    </div>
                                    <div className="mt-4 text-slate-400">→  Packaging source... <span className="text-white">Done (15ms)</span></div>
                                    <div className="mt-1 text-slate-400">→  Uploading build context... <span className="text-white">Done (450ms)</span></div>
                                    <div className="mt-1 text-slate-400">→  Building image... <span className="text-white">Done (12s)</span></div>
                                    <div className="mt-4 text-white">Production: <span className="underline decoration-emerald-500 text-emerald-400">https://myapp-xyz.inflynx.app</span></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Step 3 */}
                    <div className="relative pl-8 md:pl-16 reveal" style={{ animationDelay: '200ms' }}>
                        <div className="absolute -left-[9px] top-0 flex h-5 w-5 items-center justify-center rounded-full bg-white ring-4 ring-slate-100">
                            <div className="h-2.5 w-2.5 rounded-full bg-slate-900" />
                        </div>
                        <div className="grid gap-8 md:grid-cols-2">
                            <div>
                                <div className="text-sm font-bold uppercase tracking-widest text-slate-400">Step 03</div>
                                <h3 className="mt-2 text-3xl font-bold text-slate-900">Manage</h3>
                                <p className="mt-4 text-lg text-slate-600">
                                    View logs, rollback deployments, and map custom domains from your dashboard.
                                </p>
                            </div>
                            <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-lg">
                                {/* Abstract UI representation */}
                                <div className="flex items-center gap-4 border-b border-slate-100 pb-4">
                                    <div className="h-10 w-10 rounded-full bg-slate-100" />
                                    <div className="space-y-1">
                                        <div className="h-2 w-24 rounded bg-slate-200" />
                                        <div className="h-2 w-16 rounded bg-slate-100" />
                                    </div>
                                </div>
                                <div className="mt-4 space-y-3">
                                    <div className="flex items-center justify-between rounded p-2 bg-emerald-50 text-emerald-700 text-xs font-medium">
                                        <span>Deployment #34</span>
                                        <span>Live</span>
                                    </div>
                                    <div className="flex items-center justify-between rounded p-2 bg-slate-50 text-slate-500 text-xs">
                                        <span>Deployment #33</span>
                                        <span>Rollback</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-slate-900 py-20 text-white">
                <div className="mx-auto w-full max-w-6xl px-6 text-center">
                    <h2 className="text-3xl font-bold md:text-4xl">Under the hood</h2>
                    <p className="mx-auto mt-4 max-w-2xl text-slate-400">
                        We leverage industry-standard heavyweights so you don't have to manage them.
                    </p>
                    <div className="mt-12 grid grid-cols-2 gap-8 md:grid-cols-4 opacity-70 grayscale transition-all hover:grayscale-0">
                        {/* Logos would go here, text for now */}
                        <div className="flex items-center justify-center text-xl font-bold">Google Cloud</div>
                        <div className="flex items-center justify-center text-xl font-bold">Cloud Run</div>
                        <div className="flex items-center justify-center text-xl font-bold">Buildpacks</div>
                        <div className="flex items-center justify-center text-xl font-bold">Docker</div>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default HowItWorks
